import { flatMap } from 'lodash-es';

import { Params } from '@angular/router';

import { RouterSelectors } from '@mitsubishi/core';
import { CurrencyUtils, Study, StudySubcategory, StudyUnit } from '@mitsubishi/shared/data-access';

import { Selector, createSelector } from '@ngxs/store';

import { Project } from '../../models';
import { StudyUtils } from '../../utils/study.utils';
import { ProjectSelectors } from '../project/project.selectors';
import { PROJECT_STUDY_STATE_TOKEN, StudyStateModel } from './study.model';

export class StudySelectors {
  @Selector([RouterSelectors.params])
  static getRouteStudyId(params: Params): string {
    return params.studyId;
  }

  @Selector([StudySelectors.getRouteStudyId, ProjectSelectors.projectStudies])
  static getActiveStudy(studyId: string, studies: Study[]) {
    return studies?.find(study => study.uuid === studyId);
  }

  @Selector([StudySelectors.getActiveStudy])
  static isActiveStudyCertified(study: Study | undefined) {
    if (!study) {
      return false;
    }

    return study.certified;
  }

  @Selector([StudySelectors.getActiveStudy])
  static isActiveStudyTemporary(study: Study | undefined) {
    if (!study) {
      return false;
    }

    return study.isTemporary;
  }

  @Selector([StudySelectors.getActiveStudy])
  static activeStudyCurrencySymbol(study: Study | undefined) {
    return CurrencyUtils.getCurrencySymbolFromCode(study?.currencyCode);
  }

  @Selector([StudySelectors.getActiveStudy])
  static currentStudyOrganizationId(study: Study | undefined) {
    return study?.organizationId;
  }

  @Selector([StudySelectors.getRouteUnitId, StudySelectors.getActiveStudy])
  static getActiveUnit(unitId: string | undefined, study?: Study): StudyUnit | undefined {
    if (!study) return undefined;
    const units = flatMap(Object.values(study.subcategories), (subcategory: StudySubcategory) =>
      Object.values(subcategory.units)
    );
    return units.find((unit: StudyUnit) => unit.uuid === unitId);
  }

  @Selector([PROJECT_STUDY_STATE_TOKEN])
  static modelsAdditionProgress(state: StudyStateModel) {
    return state.modelsAdditionProgress;
  }

  @Selector([RouterSelectors.params])
  static getRouteUnitId(params: Params): string | undefined {
    return params.unitId;
  }

  @Selector([StudySelectors.getActiveStudy])
  static getGoodForSelectionUnits(study: Study | undefined) {
    if (!study) return [];

    return StudyUtils.getSubcategoriesUnitPerformanceItems({
      subcategories: Object.values(study.subcategories),
      subcategoryId: undefined,
      outputs: [],
    }).filter(item => item.isGoodForSelection);
  }

  @Selector([StudySelectors.getActiveStudy])
  static getActiveStudySubcategories(study: Study | undefined): StudySubcategory[] {
    return Object.values(study?.subcategories ?? {});
  }

  static getStudyById(studyId: string) {
    return createSelector([ProjectSelectors.current], (project: Project | undefined) => {
      if (!project) {
        return undefined;
      }

      return project.studies[studyId];
    });
  }

  static getSubcategoryByUnitId(unitId: string) {
    return createSelector([StudySelectors.getActiveStudy], (study: Study | undefined) => {
      if (!study) return;
      return Object.values(study.subcategories).find(subcategory =>
        Object.values(subcategory.units).some(unit => unit.uuid === unitId)
      );
    });
  }

  static getUnit(unitId: string | undefined) {
    return createSelector([StudySelectors.getActiveStudy], (study: Study | undefined) => {
      if (!study || !unitId) return undefined;

      const allSubcategories = Object.values(study.subcategories);
      const allUnits = StudyUtils.getSubcategoryUnits(allSubcategories);

      return allUnits[unitId];
    });
  }

  static getExternalUnit(unitId: string | undefined, workingConditionId: string) {
    return createSelector([StudySelectors.getActiveStudy], (study: Study | undefined) => {
      if (!study || !unitId) return undefined;

      const allSubcategories = Object.values(study.subcategories);
      const allUnits = StudyUtils.getSubcategoryUnits(allSubcategories);

      const externalUnitId = allUnits[unitId].workingConditionsData[workingConditionId]?.externalUnitId;

      return allUnits[externalUnitId];
    });
  }
}
